import React, { useEffect, useState } from "react";
import { ContainerCupomListagem } from "../CuponsListagem/styles";
import { Toggle } from "../../Toggle/Toggle";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { currencyMask, dateMask } from "../../../services/Masks";
import Table from "../../Table/Table";
import { Select } from "../../Select/index";
import Paginacao from "../../Paginacao";
import { Input } from "../../Input";
import { Brinde } from "../../../types/Brinde";
import moment from "moment";

interface BrindesListagemProps {
  brindes: Brinde[];
  onEditarBrinde: (index: number) => void;
  onNovoBrinde: () => void;
  onToggleBrinde: (index: number, ativo: boolean) => void;
  mostrarTexto?: boolean;
}

function normalizarBusca(texto: string) {
  return texto.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

const BrindesListagem: React.FC<BrindesListagemProps> = ({
  brindes,
  onEditarBrinde,
  onNovoBrinde,
  onToggleBrinde,
  mostrarTexto = true,
}) => {
  const { theme } = useTheme();
  const [filtroAtivo, setFiltroAtivo] = useState<"todos" | "ativos" | "inativos">("todos");
  const [busca, setBusca] = useState("");
  const [buscaDigitada, setBuscaDigitada] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itensPorPagina = 20;

  const isAtivo = (ativo: any): boolean =>
    ativo === true || ativo === 1 || ativo === "1" || ativo === "true";

  const semExpiracao = (data?: string | null): boolean =>
    !data || moment(data).year() >= 2099;

  const tableColumns = [
    { key: "foto", label: "Foto", mask: (v: any) => v },
    { key: "nome", label: "Nome do brinde", mask: (v: any) => v },
    { key: "regra", label: "Regra", mask: (v: any) => v },
    { key: "validade", label: "Validade", mask: (v: any) => v },
    { key: "acoes", label: "Ações", mask: (v: any) => v },
  ];

  const brindesFiltrados = brindes.filter((b) => {
    if (filtroAtivo === "ativos") return isAtivo(b.ativo);
    if (filtroAtivo === "inativos") return !isAtivo(b.ativo);
    return true;
  }).filter((b) => {
    const termo = normalizarBusca(busca);
    return !termo || normalizarBusca(b.nome || "").includes(termo);
  });

  const totalPaginas = Math.max(1, Math.ceil(brindesFiltrados.length / itensPorPagina));

  useEffect(() => {
    setCurrentPage(1);
  }, [filtroAtivo, busca]);

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(totalPaginas);
  }, [currentPage, totalPaginas]);

  const tableData = brindesFiltrados.map((brinde) => {
    const originalIndex = brindes.findIndex((b) => b === brinde);
    return {
      // A coluna "foto" é tratada pelo próprio Table (renderiza via ImgWithLoading
      // trocando o {size}); basta passar a URL como string.
      foto: brinde.foto || "",
      nome: (
        <span onClick={() => onEditarBrinde(originalIndex)} style={{ cursor: "pointer" }} className="fonteTexto">
          {brinde.nome}
        </span>
      ),
      regra:
        brinde.tipo_regra === "valor"
          ? `Mínimo de ${currencyMask((brinde.valor_minimo ?? 0).toString())}`
          : `Mínimo de ${brinde.quantidade_minima ?? 0} produtos`,
      validade: semExpiracao(brinde.data_expiracao) ? "Ilimitado" : dateMask(brinde.data_expiracao as string, undefined, true),
      acoes: (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Toggle value={isAtivo(brinde.ativo)} onChange={(newValue) => onToggleBrinde(originalIndex, newValue)} />
          <span onClick={() => onEditarBrinde(originalIndex)} className="editar fonteTexto">
            Editar
          </span>
        </div>
      ),
    };
  });

  const dadosPagina = tableData.slice(
    (currentPage - 1) * itensPorPagina,
    currentPage * itensPorPagina
  );

  return (
    <ContainerCupomListagem theme={theme} configIn="empresa">
      <div className="containerCupomListagem">
        {mostrarTexto && (
          <>
            <div className="textoCupom fonteTexto">
              Aumente suas vendas, seu ticket médio e fidelize seus clientes! Você pode criar brindes pelo
              valor mínimo de sacola ou quantidade de produtos.
            </div>
            <div
              className="fonteRotulos"
              style={{ color: theme.colors.tertiary, marginTop: "0.8rem", lineHeight: 1.4 }}
            >
              O cliente recebe apenas 1 brinde por sacola. Quando mais de uma regra é atingida, a de valor
              mínimo de sacola tem prioridade.
            </div>
            <div className="linha"></div>
          </>
        )}

        {brindes.length > 0 && (
          <div className="filtros-cupom">
            <div className="busca-listagem">
              <Input
                type="text"
                icone="icons/search.svg"
                placeholder="Pesquisar por nome do brinde"
                value={buscaDigitada}
                onChange={(newValue) => setBuscaDigitada(newValue)}
                onDebounce={setBusca}
                debounceTime={1000}
                onKeyDown={(event) => {
                  // a listagem fica dentro do form de configurações; Enter não deve salvar
                  if (event.key === "Enter") event.preventDefault();
                }}
              />
            </div>
            <Select
              value={filtroAtivo}
              onChange={(newValue) => setFiltroAtivo(newValue as "todos" | "ativos" | "inativos")}
              options={[
                { name: "Ativos", value: "ativos" },
                { name: "Inativos", value: "inativos" },
                { name: "Todos", value: "todos" },
              ]}
              className="select-filtro-cupons"
            />
          </div>
        )}

        {tableData.length > 0 ? (
          <Table
            columns={tableColumns}
            data={dadosPagina}
            sortable={false}
            selectable={false}
            button={false}
            page={1}
            itensPorPagina={itensPorPagina}
          />
        ) : (
          <div className="fonteTexto" style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            {brindes.length === 0
              ? "Nenhum brinde cadastrado"
              : busca.trim()
              ? "Nenhum brinde encontrado para a pesquisa"
              : "Nenhum brinde encontrado"}
          </div>
        )}

        <div className="rodapeListagem">
          <div className="paginacaoListagem">
            {totalPaginas > 1 && (
              <Paginacao
                currentPage={currentPage}
                totalPages={totalPaginas}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
          <div className="adicionarListagem">
            <div className="novo fonteTexto" onClick={() => onNovoBrinde()}>
              Adicionar um novo brinde
            </div>
          </div>
          <div></div>
        </div>
      </div>
    </ContainerCupomListagem>
  );
};

export default BrindesListagem;
